import axios from 'axios'
import type { 
  ApiResponse, 
  PaginatedResponse, 
  User, 
  Campaign, 
  Store, 
  Creative,
  StoreGroup,
  Advertiser,
  AdSlot,
  DailyStats
} from '../types'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth token
api.interceptors.request.use((config) => {
  const storage = localStorage.getItem('dtl-auth-storage')
  if (storage) {
    const { state } = JSON.parse(storage)
    if (state?.token) {
      config.headers.Authorization = `Bearer ${state.token}`
    }
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dtl-auth-storage')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const { data } = await api.post<ApiResponse<{ user: User; token: string }>>('/auth/login', { email, password })
    return data
  },
  
  logout: async () => {
    const { data } = await api.post<ApiResponse<null>>('/auth/logout')
    return data
  },
  
  me: async () => {
    const { data } = await api.get<ApiResponse<User>>('/auth/me')
    return data
  },
  
  resetPassword: async (email: string) => {
    const { data } = await api.post<ApiResponse<null>>('/auth/reset-password', { email })
    return data
  },
}

// Campaign API
export const campaignApi = {
  list: async (params?: { page?: number; limit?: number; status?: string; advertiserId?: string }) => {
    const { data } = await api.get<PaginatedResponse<Campaign>>('/campaigns', { params })
    return data
  },
  
  get: async (id: string) => {
    const { data } = await api.get<ApiResponse<Campaign>>(`/campaigns/${id}`)
    return data
  },
  
  create: async (campaign: Partial<Campaign>) => {
    const { data } = await api.post<ApiResponse<Campaign>>('/campaigns', campaign)
    return data
  },
  
  update: async (id: string, campaign: Partial<Campaign>) => {
    const { data } = await api.put<ApiResponse<Campaign>>(`/campaigns/${id}`, campaign)
    return data
  },
  
  delete: async (id: string) => {
    const { data } = await api.delete<ApiResponse<null>>(`/campaigns/${id}`)
    return data
  },
  
  updateStatus: async (id: string, status: Campaign['status']) => {
    const { data } = await api.patch<ApiResponse<Campaign>>(`/campaigns/${id}/status`, { status })
    return data
  },
}

// Store API
export const storeApi = {
  list: async (params?: { page?: number; limit?: number; prefecture?: string; status?: string; groupId?: string }) => {
    const { data } = await api.get<PaginatedResponse<Store>>('/stores', { params })
    return data
  },
  
  get: async (id: string) => {
    const { data } = await api.get<ApiResponse<Store>>(`/stores/${id}`)
    return data
  },
  
  create: async (store: Partial<Store>) => {
    const { data } = await api.post<ApiResponse<Store>>('/stores', store)
    return data
  },
  
  update: async (id: string, store: Partial<Store>) => {
    const { data } = await api.put<ApiResponse<Store>>(`/stores/${id}`, store)
    return data
  },
  
  delete: async (id: string) => {
    const { data } = await api.delete<ApiResponse<null>>(`/stores/${id}`)
    return data
  },
}

// Store Group API
export const storeGroupApi = {
  list: async () => {
    const { data } = await api.get<ApiResponse<StoreGroup[]>>('/store-groups')
    return data
  },
  
  get: async (id: string) => {
    const { data } = await api.get<ApiResponse<StoreGroup>>(`/store-groups/${id}`)
    return data
  },
  
  create: async (group: Partial<StoreGroup>) => {
    const { data } = await api.post<ApiResponse<StoreGroup>>('/store-groups', group)
    return data
  },
  
  update: async (id: string, group: Partial<StoreGroup>) => {
    const { data } = await api.put<ApiResponse<StoreGroup>>(`/store-groups/${id}`, group)
    return data
  },
  
  addStores: async (groupId: string, storeIds: string[]) => {
    const { data } = await api.post<ApiResponse<null>>(`/store-groups/${groupId}/stores`, { storeIds })
    return data
  },
  
  removeStores: async (groupId: string, storeIds: string[]) => {
    const { data } = await api.delete<ApiResponse<null>>(`/store-groups/${groupId}/stores`, { data: { storeIds } })
    return data
  },
}

// Creative API
export const creativeApi = {
  list: async (campaignId: string) => {
    const { data } = await api.get<ApiResponse<Creative[]>>(`/campaigns/${campaignId}/creatives`)
    return data
  },
  
  upload: async (campaignId: string, formData: FormData) => {
    const { data } = await api.post<ApiResponse<Creative>>(`/campaigns/${campaignId}/creatives`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },
  
  update: async (id: string, creative: Partial<Creative>) => {
    const { data } = await api.put<ApiResponse<Creative>>(`/creatives/${id}`, creative)
    return data
  },
  
  delete: async (id: string) => {
    const { data } = await api.delete<ApiResponse<null>>(`/creatives/${id}`)
    return data
  },
  
  review: async (id: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    const { data } = await api.patch<ApiResponse<Creative>>(`/creatives/${id}/review`, { status, rejectionReason })
    return data
  },
}

// Advertiser API
export const advertiserApi = {
  list: async () => {
    const { data } = await api.get<ApiResponse<Advertiser[]>>('/advertisers')
    return data
  },
  
  get: async (id: string) => {
    const { data } = await api.get<ApiResponse<Advertiser>>(`/advertisers/${id}`)
    return data
  },
  
  create: async (advertiser: Partial<Advertiser>) => {
    const { data } = await api.post<ApiResponse<Advertiser>>('/advertisers', advertiser)
    return data
  },
  
  update: async (id: string, advertiser: Partial<Advertiser>) => {
    const { data } = await api.put<ApiResponse<Advertiser>>(`/advertisers/${id}`, advertiser)
    return data
  },
}

// Ad Slot API
export const adSlotApi = {
  list: async () => {
    const { data } = await api.get<ApiResponse<AdSlot[]>>('/ad-slots')
    return data
  },
  
  get: async (id: string) => {
    const { data } = await api.get<ApiResponse<AdSlot>>(`/ad-slots/${id}`)
    return data
  },
}

// Report API
export const reportApi = {
  summary: async (params: { startDate: string; endDate: string }) => {
    const { data } = await api.get<ApiResponse<DailyStats>>('/reports/summary', { params })
    return data
  },
  
  daily: async (params: { startDate: string; endDate: string; campaignId?: string; storeId?: string }) => {
    const { data } = await api.get<ApiResponse<DailyStats[]>>('/reports/daily', { params })
    return data
  },
  
  campaigns: async (params: { startDate: string; endDate: string }) => {
    const { data } = await api.get<ApiResponse<{ campaignId: string; campaignName: string; stats: DailyStats }[]>>('/reports/campaigns', { params })
    return data
  },
  
  stores: async (params: { startDate: string; endDate: string }) => {
    const { data } = await api.get<ApiResponse<{ storeId: string; storeName: string; stats: DailyStats }[]>>('/reports/stores', { params })
    return data
  },
}

export default api

